import React from "react";
import { useIntl } from "react-intl";
import { Box, Divider, Grid, Stack, TextField, Typography } from "@mui/material";
import { FieldGroupTitle, MasterField, SectionHeader } from "../../components/primitives";
import { dividerSx, inputSx, panelSx, ui } from "../../styles";
import OrganizationLinkSection from "./OrganizationLinkSection";

const CONTACT_FIELDS = [
  ["contact", "admin.crm.customerCard.fields.contactPerson"],
  ["contactEmail", "admin.crm.customerCard.fields.email"],
  ["contactTelephone", "admin.crm.customerCard.fields.telephone"],
];

const OrganizationStep = ({ availableOrganizations, baseline = {}, draft = {}, addressSourceError,
  addressSourceLoading, onOpenOrganization, onOrganizationChange, selectedOrganization, onReset, updateField }) => {
  const intl = useIntl(), t = (id, values) => intl.formatMessage({ id }, values);
  const companyFields = [
    ["companyName", "admin.crm.customerCard.fields.companyName", 4, true],
    ["orgNo", "admin.crm.customerCard.fields.organizationNumber", 3, false],
    ["vatRegNo", "admin.crm.customerCard.fields.vatNumber", 3, false],
  ];
  const renderMaster = ([field, labelId, md, required = false]) => <Grid item xs={12} sm={6} md={md} key={field}>
    <MasterField required={required } field={field} label={t(labelId)} masterValue={baseline[field]} onChange={(evt) => updateField(field, evt.target.value)} onReset={evt => onReset(field)} value={draft[field]} />
  </Grid>;

  return <Box>
    <SectionHeader title={t("admin.crm.customerCard.organization.title")} description={t("admin.crm.customerCard.organization.description")} />

    {selectedOrganization && (addressSourceLoading || addressSourceError) && <Stack direction="row" spacing={.55} alignItems="center"
      sx={{ mt: -.55, mb: .7, color: addressSourceError ? ui.danger : ui.subtle }}>
      <Typography sx={{ fontSize: 8.7, lineHeight: 1.35 }}>{t(addressSourceLoading ? "admin.crm.customerCard.organization.loadingAddresses" : "admin.crm.customerCard.organization.addressLoadError")}</Typography>
    </Stack>}

    <Box sx={panelSx}>
      <FieldGroupTitle>{t("admin.crm.customerCard.organization.companyDetails")}</FieldGroupTitle>
      <Grid container spacing={.9}>
        {renderMaster(companyFields[0])}
        <Grid item xs={12} sm={6} md={2}><TextField fullWidth label={t("admin.crm.customerCard.fields.customerNumber")} value={draft.customerNo ?? ""} onChange={evt => updateField("customerNo", evt.target.value)} required sx={inputSx} /></Grid>
        {companyFields.slice(1).map(renderMaster)}
      </Grid>

      <Divider sx={{ ...dividerSx, my: .85 }} /><FieldGroupTitle>{t("admin.crm.customerCard.organization.contact")}</FieldGroupTitle>
      <Grid container spacing={.9}>{CONTACT_FIELDS.map(([field, labelId]) => <Grid item xs={12} sm={4} key={field}>
        <MasterField field={field} label={t(labelId)} masterValue={baseline[field]} onChange={(evt) => updateField(field, evt.target.value)} onReset={evt=> onReset(field)} value={draft[field]} />
      </Grid>)}</Grid>

      {/*<Divider sx={{ ...dividerSx, my:.85 }}/><FieldGroupTitle>{t("admin.crm.customerCard.organization.customerRelationship")}</FieldGroupTitle>
      <Grid container spacing={.75}>
        <Grid item xs={12} sm={6} md={4}><TextField fullWidth label={t("admin.crm.customerCard.fields.customerOwner")} value={draft.customerOwner} onChange={updateField("customerOwner")} placeholder={t("admin.crm.customerCard.fields.customerOwnerPlaceholder")} sx={compactInputSx}/></Grid>
        <Grid item xs={12} sm={3} md={4}><TextField fullWidth select label={t("admin.crm.customerCard.fields.status")} value={draft.customerStatus} onChange={updateField("customerStatus")} sx={compactInputSx}><OptionItems options={CUSTOMER_STATUS_OPTIONS}/></TextField></Grid>
        <Grid item xs={12} sm={3} md={4}><TextField fullWidth type="date" label={t("admin.crm.customerCard.fields.activeFrom")} value={draft.activeFrom} onChange={updateField("activeFrom")} InputLabelProps={{ shrink:true }} sx={compactInputSx}/></Grid>
        <Grid item xs={12}><TextField fullWidth multiline minRows={1} maxRows={3} label={t("admin.crm.customerCard.fields.relationshipNote")} value={draft.relationshipNote} onChange={updateField("relationshipNote")} sx={compactMultilineSx}/></Grid>
      </Grid>*/}

      <Divider sx={{ ...dividerSx, my: .85 }} />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.55fr) minmax(280px, .85fr)" }, gap: { xs: .75, lg: 1.2 }, alignItems: "center" }}>
        <OrganizationLinkSection availableOrganizations={availableOrganizations} baseline={baseline} draft={draft} onOpenOrganization={onOpenOrganization}
          onOrganizationChange={onOrganizationChange} selectedOrganization={selectedOrganization} />


        {/*
        <Stack direction="row" spacing={.7} alignItems="center" sx={{
          minWidth: 0, pt: { xs: .75, lg: 0 }, borderTop: { xs: `1px solid ${ui.border}`, lg: "none" },
          pl: { xs: 0, lg: 1.2 }, borderLeft: { xs: "none", lg: `1px solid ${ui.border}` }
        }}>
          <CustomerLogo src={logoSrc} size={34} fallbackIconSize={16} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={.45} alignItems="center" sx={{ flexWrap: "wrap" }}><Typography sx={{ color: ui.text, fontSize: 10.2, fontWeight: 600 }}>{t("admin.crm.customerCard.logo.title")}</Typography>
              <OverrideCountChip count={countLogoOverrides(draft, baseline)} label={t("admin.crm.customerCard.logo.custom")} /></Stack>
            <Typography sx={{ mt: .02, color: ui.subtle, fontSize: 8.9 }}>{t("admin.crm.customerCard.logo.description")}</Typography>
            {logoError && <Typography sx={{ mt: .12, color: ui.danger, fontSize: 9 }}>{logoError}</Typography>}
          </Box>
          <Stack direction="row" spacing={.35}>
            <Button component="label" size="small" variant="text" startIcon={<ImageOutlinedIcon sx={{ fontSize: 14 }} />} sx={{ minHeight: 27, px: .5, color: "primary.main", textTransform: "none", fontSize: 9.5, fontWeight: 600 }}>
              {t(draft.logoDataUrl ? "admin.crm.customerCard.logo.change" : "admin.crm.customerCard.logo.add")}<input hidden type="file" accept=".png,.jpg,.jpeg,.webp,.svg,image/png,image/jpeg,image/webp,image/svg+xml" onChange={onLogoFile} />
            </Button>
            {(draft.logoDataUrl || draft.logoHidden) && <Tooltip title={t("admin.crm.customerCard.logo.resetTooltip")}><IconButton size="small" onClick={onResetLogo} sx={{ width: 27, height: 27, color: ui.muted }}><RestartAltRoundedIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>}
          </Stack>
        </Stack>*/}
      </Box>
    </Box>
  </Box>;
};

export default OrganizationStep;
